import TableRow from "@mui/material/TableRow";
import { useContext, useEffect, useMemo, useState } from "react";
import { Material, MaterialOutsourceStandard } from "../../../../data/entities";
import { ROLE } from "../../../../data/enums";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import CustomTable from "../../../../shared/components/CustomTable";
import { StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import IconButton from "../../../../shared/components/IconButton";
import { SearchBarWithSingleSelectSuggestion } from "../../../../shared/components/SearchBar/SearchBarWithSuggestion";
import TextBox from "../../../../shared/components/TextBox";
import CustomButton from "../../../../shared/components/TitleRow/CustomButton";
import { api } from "../../../../shared/hooks/api";
import { ContentWrapper, StyledNavLink } from "../../../../shared/styled/TableStyles";
import DeleteIcon from "../../../../shared/svg/DeleteIcon";
import { formatInternationalNumber, parseInternationalToNumber } from "../../../../utils/format";
import { error, success } from "../../../../utils/toast";
import { MaterialButtonRow } from "../../../Provider/create/Content/MaterialList/Styled";
import { TableItemCounter, TableTitle } from "../../single/MaterialList/Styled";

const OutsourceStandardColumns = [
    { text: "Mã vật tư nguồn", icon: <></> },
    { text: "Tên vật tư nguồn", icon: <></> },
    { text: "Số lượng / 1 đơn vị", icon: <></> },
    { text: "Đơn vị", icon: <></> },
    { text: "Ghi chú", icon: <></> },
    { text: "", icon: <></> },
];

const getMaterialId = (material?: Material | null) => material?.id ? String(material.id) : "";

const OutsourceStandardRow = ({ item, extraRowProps }: any) => (
    <TableRow>
        <StyledTableCell align="left">
            <StyledNavLink to={`/materials/${item.source_material?.id}/`}>
                {item.source_material?.material_code || "Chưa có"}
            </StyledNavLink>
        </StyledTableCell>
        <StyledTableCell align="left">{item.source_material?.name || "Chưa có"}</StyledTableCell>
        <StyledTableCell align="left">
            <TextBox
                numeric
                text={formatInternationalNumber(item.source_quantity_per_output ?? "")}
                onChangeText={(value: string) => {
                    const n = parseInternationalToNumber(value, error);
                    if (Number.isFinite(n)) {
                        extraRowProps.onChangeQuantity(item, n);
                    }
                }}
                disabled={!extraRowProps.canEdit}
            />
        </StyledTableCell>
        <StyledTableCell align="left">{item.source_material?.measurement_unit || ""}</StyledTableCell>
        <StyledTableCell align="left">
            <TextBox
                text={item.note || ""}
                onChangeText={(value: string) => extraRowProps.onChangeNote(item, value)}
                disabled={!extraRowProps.canEdit}
            />
        </StyledTableCell>
        <StyledTableCell align="left">
            <IconButton
                onClick={() => extraRowProps.onRemove(item)}
                disabled={!extraRowProps.canEdit}
            >
                <DeleteIcon />
            </IconButton>
        </StyledTableCell>
    </TableRow>
);

interface MaterialOutsourceStandardTabProps {
    material: Material;
}

const MaterialOutsourceStandardTab = ({ material }: MaterialOutsourceStandardTabProps) => {
    const materialId = getMaterialId(material);
    const [standards, setStandards] = useState<MaterialOutsourceStandard[]>([]);
    const [isDirty, setIsDirty] = useState(false);
    const {
        user: { role },
    } = useContext(AuthenticationContext);
    const canEdit = [ROLE.MANAGER, ROLE.LOGISTIC_STAFF, ROLE.LOGISTIC_MANAGER, ROLE.PURCHASING_STAFF].includes(role);

    const [
        { data: standardsData, isLoading, setLocalUrl: setStandardsUrl },
        fetchStandards,
    ] = api.get<MaterialOutsourceStandard[]>(
        `/materials/${materialId}/outsource-standards/`,
        {},
        !materialId,
    );
    const [{ data: allMaterialsData }, fetchAllMaterials] = api.get("/materials/");
    const [{ isUpdating, setLocalUrl: setUpdateUrl }, updateStandards] = api.put(
        `/materials/${materialId}/outsource-standards/`,
    );

    useEffect(() => {
        if (!materialId) return;
        setStandardsUrl(`/materials/${materialId}/outsource-standards/`);
        setUpdateUrl(`/materials/${materialId}/outsource-standards/`);
    }, [materialId, setStandardsUrl, setUpdateUrl]);

    useEffect(() => {
        if (!Array.isArray(standardsData)) return;
        setStandards(standardsData);
        setIsDirty(false);
    }, [standardsData]);

    const allMaterials = useMemo(() => {
        const materials = allMaterialsData?.results || allMaterialsData || [];
        const selectedIds = new Set(
            standards.map((item) => getMaterialId(item.source_material)).filter(Boolean),
        );
        return materials.filter((item: Material) => {
            const id = getMaterialId(item);
            return id && id !== materialId && !selectedIds.has(id);
        });
    }, [allMaterialsData, materialId, standards]);

    const updateRows = (rows: MaterialOutsourceStandard[]) => {
        setStandards(rows);
        setIsDirty(true);
    };

    const onAddMaterial = (sourceMaterial: Material) => {
        const sourceMaterialId = getMaterialId(sourceMaterial);
        if (!sourceMaterialId || sourceMaterialId === materialId) {
            error("Không thể thêm vật tư nguồn", "Vật tư nguồn không được trùng vật tư hiện tại");
            return;
        }
        if (standards.some((item) => getMaterialId(item.source_material) === sourceMaterialId)) {
            error("Không thể thêm vật tư nguồn", "Vật tư nguồn đã có trong định mức");
            return;
        }
        updateRows([
            ...standards,
            {
                source_material: sourceMaterial,
                source_quantity_per_output: 1,
                note: "",
            },
        ]);
    };

    const onRemove = (standard: MaterialOutsourceStandard) => {
        updateRows(
            standards.filter(
                (item) => getMaterialId(item.source_material) !== getMaterialId(standard.source_material),
            ),
        );
    };

    const onChangeQuantity = (standard: MaterialOutsourceStandard, quantity: number) => {
        updateRows(
            standards.map((item) =>
                getMaterialId(item.source_material) === getMaterialId(standard.source_material)
                    ? { ...item, source_quantity_per_output: quantity }
                    : item,
            ),
        );
    };

    const onChangeNote = (standard: MaterialOutsourceStandard, note: string) => {
        updateRows(
            standards.map((item) =>
                getMaterialId(item.source_material) === getMaterialId(standard.source_material)
                    ? { ...item, note }
                    : item,
            ),
        );
    };

    const validateRows = () => {
        const sourceIds = new Set<string>();
        for (const standard of standards) {
            const sourceMaterialId = getMaterialId(standard.source_material);
            if (!sourceMaterialId || sourceMaterialId === materialId) return false;
            if (sourceIds.has(sourceMaterialId)) return false;
            sourceIds.add(sourceMaterialId);
            if (!Number.isFinite(Number(standard.source_quantity_per_output)) || Number(standard.source_quantity_per_output) <= 0) {
                return false;
            }
        }
        return true;
    };

    const tableItems = standards.map((item) => ({
        ...item,
        client_uid: getMaterialId(item.source_material) || item.id,
    }));

    const onSave = async () => {
        if (!validateRows()) {
            error("Không thể lưu định mức", "Vui lòng kiểm tra vật tư nguồn và số lượng lớn hơn 0");
            return;
        }
        try {
            await updateStandards(
                standards.map((item) => ({
                    source_material_id: getMaterialId(item.source_material),
                    source_quantity_per_output: Number(item.source_quantity_per_output),
                    note: item.note || "",
                })),
            );
            await fetchStandards();
            success("THÀNH CÔNG", "Cập nhật định mức gia công thành công");
        } catch (err) {
            error("Cập nhật định mức gia công thất bại", err);
        }
    };

    return (
        <ContentWrapper>
            <TableTitle>
                Định mức gia công
                <TableItemCounter> ({standards.length} vật tư nguồn)</TableItemCounter>
            </TableTitle>

            {canEdit && (
                <SearchBarWithSingleSelectSuggestion
                    menuList={allMaterials}
                    fetchItems={(variables: any) => fetchAllMaterials({ ...variables, page_size: 50 })}
                    handleOnclickItem={onAddMaterial}
                />
            )}

            <CustomTable
                itemList={tableItems}
                RowElement={OutsourceStandardRow}
                TableColumns={OutsourceStandardColumns}
                extraRowProps={{
                    canEdit,
                    onRemove,
                    onChangeQuantity,
                    onChangeNote,
                }}
            />

            {!isLoading && standards.length === 0 && "Chưa có định mức gia công"}

            {canEdit && (
                <MaterialButtonRow>
                    <CustomButton
                        title="Lưu định mức gia công"
                        onClick={onSave}
                        variant="contained"
                        disabled={isUpdating || !isDirty}
                    />
                </MaterialButtonRow>
            )}
        </ContentWrapper>
    );
};

export default MaterialOutsourceStandardTab;
